import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePostHog } from "posthog-js/react";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-white hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-primary text-primary bg-transparent shadow-sm hover:bg-primary/10",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-primary/10 text-primary",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  trackingEvent?: string;
  trackingProperties?: Record<string, unknown>;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      isLoading,
      trackingEvent,
      trackingProperties,
      onClick,
      ...props
    },
    ref
  ) => {
    const posthog = usePostHog();
    const Comp = asChild ? Slot : "button";

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Don't track clicks when loading
      if (!isLoading && posthog) {
        if (trackingEvent) {
          posthog.capture(trackingEvent, trackingProperties);
        } else if (typeof children === "string") {
          posthog.capture("button_clicked", {
            label: children,
            variant,
            ...trackingProperties,
          });
        }
      }
      onClick?.(e);
    };

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading...
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };

// import React from 'react';
// import { Slot } from '@radix-ui/react-slot';
// import { cva, type VariantProps } from 'class-variance-authority';
// import { Loader2 } from 'lucide-react';
// import { cn } from '@/lib/utils';

// const buttonVariants = cva(
//   'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50',
//   {
//     variants: {
//       variant: {
//         default: 'bg-primary text-white hover:bg-primary/90',
//         destructive:
//           'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
//         outline:
//           'border border-primary text-primary bg-transparent shadow-sm hover:bg-primary/10',
//         secondary:
//           'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
//         ghost: 'hover:bg-primary/10 text-primary',
//         link: 'text-primary underline-offset-4 hover:underline'
//       },
//       size: {
//         default: 'h-9 px-4 py-2',
//         sm: 'h-8 rounded-md px-3 text-xs',
//         lg: 'h-10 rounded-md px-8',
//         icon: 'h-9 w-9'
//       }
//     },
//     defaultVariants: {
//       variant: 'default',
//       size: 'default'
//     }
//   }
// );

// export interface ButtonProps
//   extends React.ButtonHTMLAttributes<HTMLButtonElement>,
//     VariantProps<typeof buttonVariants> {
//   asChild?: boolean;
//   isLoading?: boolean;
// }

// const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
//   (
//     {
//       className,
//       variant,
//       size,
//       asChild = false,
//       children,
//       isLoading,
//       ...props
//     },
//     ref
//   ) => {
//     const Comp = asChild ? Slot : 'button';
//     return (
//       <Comp
//         className={cn(buttonVariants({ variant, size, className }))}
//         ref={ref}
//         {...props}
//       >
//         {isLoading ? (
//           <>
//             <Loader2 className='mr-2 h-4 w-4 animate-spin' />
//             Loading...
//           </>
//         ) : (
//           children
//         )}
//       </Comp>
//     );
//   }
// );
// Button.displayName = 'Button';

// export { Button, buttonVariants };
